import { getEntityById } from '@core/utils/collection';
import type { OrderItem } from '../../api/order';
import type { Size } from '../../api/size';
import { SizeMode } from '../enum/sizeMode';
import { VestmentCategory } from '../enum/vestmentCategory';
import { VestmentType, getCategory } from '../enum/vestmentType';
import { getAlbFeatures } from './alb';
import { getChasubleFeatures } from './chasuble';
import { getFullName } from './customer';
import { formatMeasurement } from './measurement';
import { hasMeasurementOverrides } from './orderItem/defaults';
import {
  type SizeVestmentValue,
  isSelectableStoleVestmentType,
} from './vestment';

export const CUSTOMER_SIZE_ID = '-1';
export const CUSTOM_SIZE_ID = '0';

export function isCustomerSize(sizeId: string): boolean {
  return sizeId === CUSTOMER_SIZE_ID;
}

export function isCustomSize(sizeId: string): boolean {
  return sizeId === CUSTOM_SIZE_ID;
}

export function forCustomerForSizeSelection(
  sizeId: string,
  orderCustomerId?: string | number | null
): string {
  if (!isCustomerSize(sizeId)) {
    return '';
  }
  if (orderCustomerId == null || orderCustomerId === '') {
    return '';
  }
  return String(orderCustomerId);
}

export function resolveVestmentCategory(
  vestmentType?: VestmentType | null,
  vestmentCategory?: VestmentCategory | null
): VestmentCategory | undefined {
  if (vestmentType != null) {
    return getCategory(vestmentType);
  }
  return vestmentCategory ?? undefined;
}

export function isChasubleCategory(
  vestmentType?: VestmentType | null,
  vestmentCategory?: VestmentCategory | null
): boolean {
  return (
    resolveVestmentCategory(vestmentType, vestmentCategory) ===
    VestmentCategory.Chasuble
  );
}

export function filterSizesByCategory(
  sizes: Size[],
  category: VestmentCategory
): Size[] {
  return sizes.filter((size) => size.vestmentCategory === category);
}

export function getSizeVestmentValue(
  size?: Pick<Size, 'vestmentCategory' | 'vestmentType'> | null
): SizeVestmentValue | '' {
  if (size?.vestmentCategory === VestmentCategory.Stole) {
    if (size.vestmentType && isSelectableStoleVestmentType(size.vestmentType)) {
      return {
        vestmentCategory: VestmentCategory.Stole,
        vestmentType: size.vestmentType,
      };
    }
    return '';
  }
  if (size?.vestmentCategory) {
    return { vestmentCategory: size.vestmentCategory, vestmentType: null };
  }
  return '';
}

export function filterSizesForVestment(
  sizes: Size[],
  category: VestmentCategory,
  vestmentType?: VestmentType | null
): Size[] {
  const categorySizes = filterSizesByCategory(sizes, category);
  if (category !== VestmentCategory.Stole) {
    return categorySizes;
  }
  if (vestmentType == null) {
    return [];
  }
  return categorySizes.filter((size) => size.vestmentType === vestmentType);
}

export function getPresetSize(sizes: Size[], sizeId: string): Size | undefined {
  if (!sizeId || isCustomerSize(sizeId) || isCustomSize(sizeId)) {
    return undefined;
  }
  return getEntityById(sizes, sizeId);
}

export function getPresetSizeForCategory(
  sizes: Size[],
  category: VestmentCategory,
  sizeId: string
): Size | undefined {
  return getPresetSize(filterSizesByCategory(sizes, category), sizeId);
}

export type SizeMeasurementField =
  | 'length'
  | 'shoulder'
  | 'sleeve'
  | 'neck'
  | 'chest'
  | 'waist'
  | 'figure'
  | 'head';

export const SIZE_FIELDS_BY_VESTMENT_CATEGORY: Record<
  VestmentCategory,
  SizeMeasurementField[]
> = {
  [VestmentCategory.Chasuble]: ['length', 'sleeve'],
  [VestmentCategory.Alb]: [
    'length',
    'shoulder',
    'sleeve',
    'neck',
    'chest',
    'waist',
    'figure',
  ],
  [VestmentCategory.Stole]: ['length'],
  [VestmentCategory.Mitre]: ['head'],
};

export const SIZE_FIELD_ABBREVIATIONS: Record<SizeMeasurementField, string> = {
  length: 'L',
  shoulder: 'Sh',
  sleeve: 'Sl',
  neck: 'N',
  chest: 'C',
  waist: 'W',
  figure: 'F',
  head: 'H',
};

export const SIZE_FIELD_LABELS: Record<SizeMeasurementField, string> = {
  length: 'Length (in)',
  shoulder: 'Shoulder (in)',
  sleeve: 'Sleeve (in)',
  neck: 'Neck (in)',
  chest: 'Chest (in)',
  waist: 'Waist (in)',
  figure: 'Figure (in)',
  head: 'Head (in)',
};

export function formatSizePresetSummary(size: Size): string {
  const fields = SIZE_FIELDS_BY_VESTMENT_CATEGORY[size.vestmentCategory] ?? [];
  return fields
    .map((field) => {
      const abbreviation = SIZE_FIELD_ABBREVIATIONS[field];
      const value = size[field];
      return `${abbreviation} ${formatMeasurement(value) ?? '-'}`;
    })
    .join(' • ');
}

export function getSizeLabel(
  item: Pick<OrderItem, 'size' | 'forCustomer' | 'sizeMode' | 'vestment'> &
    Partial<Record<SizeMeasurementField, number | null | undefined>>,
  adjustedSuffix?: string,
  {
    showCustomerName = true,
    sizes = [],
  }: {
    showCustomerName?: boolean;
    sizes?: Size[];
  } = {}
): string {
  const fields = getSizeFieldsForOrderItem(item);
  const size = getEntityById(sizes, String(item.size?.id));
  const customer = item.forCustomer ?? null;
  const vestmentType = item.vestment?.type ?? null;
  const hasOverrides = hasMeasurementOverrides(fields, item, {
    size,
    customer,
    vestmentType,
    vestmentCategory: resolveVestmentCategory(vestmentType),
  });

  let label: string;
  if (showCustomerName && item.sizeMode === SizeMode.Customer && customer) {
    label = getFullName(customer);
  } else if (
    item.sizeMode === SizeMode.Customer ||
    item.sizeMode === SizeMode.Custom
  ) {
    label = 'Custom';
  } else {
    label = item.size?.name ?? '';
  }

  return hasOverrides && label && label !== 'Custom' && adjustedSuffix
    ? `${label} ${adjustedSuffix}`
    : label;
}

export function getSizeFields(
  category: VestmentCategory,
  vestmentType?: VestmentType | null
): SizeMeasurementField[] {
  switch (category) {
    case VestmentCategory.Chasuble:
      return chasubleSizeFields(vestmentType);
    case VestmentCategory.Alb:
      return albSizeFields(vestmentType);
    default:
      return SIZE_FIELDS_BY_VESTMENT_CATEGORY[category] ?? [];
  }
}

export function chasubleSizeFields(
  vestmentType?: VestmentType | null
): SizeMeasurementField[] {
  return (
    getChasubleFeatures(vestmentType)?.sizeFields ??
    SIZE_FIELDS_BY_VESTMENT_CATEGORY[VestmentCategory.Chasuble]
  );
}

export function albSizeFields(
  vestmentType?: VestmentType | null
): SizeMeasurementField[] {
  return (
    getAlbFeatures(vestmentType)?.sizeFields ??
    SIZE_FIELDS_BY_VESTMENT_CATEGORY[VestmentCategory.Alb]
  );
}

export function getSizeFieldsForOrderItem(
  item: Pick<OrderItem, 'vestment'> &
    Partial<Record<SizeMeasurementField, unknown>>
): SizeMeasurementField[] {
  const vestmentType = item.vestment?.type;
  if (!vestmentType) {
    return [];
  }
  return getSizeFields(getCategory(vestmentType), vestmentType);
}
